import { AppDispatch, useAppDispatch, useAppSelector } from 'app/store';
import { fetchAudioForPlayingEffect } from 'app/store/AudioPlayer/effects';
import { AudioPlayerAudioInstance } from 'app/store/AudioPlayer/slice.types';
import axios from 'axios';
import WaveformData from 'waveform-data';
import { addAlertAction } from 'app/store/Alerts/slice';
import { getAudioPlayer } from 'app/store/AudioPlayer/selectors';
import {
  resetCurrentAudioErrorStateAction,
  setIsPlayerReadyAction,
  setIsPlayingAction,
  setIsWaveformError,
  setIsWaveformOpen,
  setPlaylistLoadingStateAction,
  setPlayOnInitialLoadAction,
  togglePlayerOpenAction,
} from 'app/store/AudioPlayer/slice';
import { useCallback, useEffect, useRef } from 'react';
import { CustomFetchAudioForPlayingEffect } from 'app/store/Shares/slice.types';

const TARGETED_POINT_COUNT = 150;
const SCALE_POINT = 0.1;

export const loadWaveformData = async (dispatch: AppDispatch, src?: string) => {
  dispatch(setIsWaveformError(false));
  if (!src) {
    dispatch(setIsWaveformError(true));
    return null;
  }
  try {
    const { data } = await axios.get(src, {
      responseType: 'arraybuffer',
    });
    const wf = await WaveformData.create(data);
    return wf
      .channel(0)
      .max_array()
      .filter((peak) => peak >= 0);
  } catch (e: any) {
    dispatch(setIsWaveformError(true));
    dispatch(setIsWaveformOpen(false));
    dispatch(
      addAlertAction({
        message: 'Unable to load waveform. Please try again later',
      })
    );
    return null;
  }
};

const getReducedPeaks = (peaks: number[]) => {
  const reduceBy = Math.max(Math.floor(peaks.length / TARGETED_POINT_COUNT), 1);
  const reducedWaveform = peaks.filter(
    (_, index) => (index + 1) % reduceBy === 0
  );

  const smaller = reducedWaveform.map((peak) => Math.floor(peak * SCALE_POINT));
  return [reducedWaveform, smaller];
};

export const useAudioLoading = ({
  customFetchAudioForPlayingEffect,
}: {
  customFetchAudioForPlayingEffect?: CustomFetchAudioForPlayingEffect;
}) => {
  const { player } = window;

  const {
    queue,
    queueIndex,
    isPlaying,
    playOnInitialLoad,
    playMode,
  } = useAppSelector(getAudioPlayer);
  const currentAudio = queue[queueIndex];
  const dispatch = useAppDispatch();
  const latestCurrentAudio = useRef(currentAudio);
  const latestPlayOnInitialLoad = useRef(playOnInitialLoad);

  const handleAudioFetch = useCallback(
    async (audioToFetch: AudioPlayerAudioInstance) => {
      if (!audioToFetch || audioToFetch?.isError) {
        return;
      }
      const shouldFetchAudio =
        !audioToFetch?.audioSrc && !audioToFetch?.loading;

      if (shouldFetchAudio) {
        player?.pause();
        player?.empty();

        const fetchEffect =
          customFetchAudioForPlayingEffect || fetchAudioForPlayingEffect;
        await dispatch(fetchEffect(audioToFetch));
      }
    },
    [dispatch, customFetchAudioForPlayingEffect, player]
  );

  const handleAudioLoadAndPlay = useCallback(
    async (audioToLoad: AudioPlayerAudioInstance) => {
      if (!audioToLoad || !audioToLoad?.audioSrc) {
        return;
      }
      dispatch(setIsPlayerReadyAction(false));

      const { audioSrc, waveformSrc } = audioToLoad;

      const waveform = await loadWaveformData(dispatch, waveformSrc);

      const peaks = getReducedPeaks(waveform || []);
      await player?.load(audioSrc, peaks);

      if (!latestPlayOnInitialLoad.current) {
        dispatch(setPlayOnInitialLoadAction(true));
        return;
      }

      dispatch(setIsPlayingAction(true));
      dispatch(togglePlayerOpenAction(true));
      const isTheAudioStillTheSame =
        audioSrc === latestCurrentAudio.current?.audioSrc;
      if (audioSrc && isTheAudioStillTheSame) {
        player?.play().catch(() => {
          // proceed
        });
      }
    },
    [dispatch, player]
  );

  // Handles audio fetching
  useEffect(() => {
    handleAudioFetch(currentAudio);
    latestCurrentAudio.current = currentAudio;
  }, [currentAudio, handleAudioFetch]);

  useEffect(() => {
    latestPlayOnInitialLoad.current = playOnInitialLoad;
  }, [playOnInitialLoad]);

  // Handles audio loading & playing
  useEffect(() => {
    handleAudioLoadAndPlay(currentAudio);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAudio?.audioSrc, handleAudioLoadAndPlay]);

  useEffect(() => {
    // Always reset audio error state on play to attempt to refetch
    if (isPlaying && currentAudio?.isError) {
      dispatch(resetCurrentAudioErrorStateAction());
    }
  }, [dispatch, isPlaying, currentAudio?.isError]);

  useEffect(() => {
    if (playMode !== 'playlist') {
      dispatch(
        setPlaylistLoadingStateAction({
          loadingPlaylistGuid: undefined,
          loadingPlaylist: undefined,
        })
      );
    }
  }, [playMode, dispatch]);
};
